import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import DocumentSelector from './DocumentSelector';
import { getStageData, saveStageData, completeStage } from '../../../api/claims';

const ClaimDefinitionModal = ({ open, onOpenChange, claimId, onComplete }) => {
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch stage data
  const { data: stageDataResponse, isLoading } = useQuery({
    queryKey: ['stageData', claimId, 'claim_definition'],
    queryFn: () => getStageData(claimId, 'claim_definition'),
    enabled: open && !!claimId
  });

  const stageData = stageDataResponse?.data || {};

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data) => saveStageData(claimId, 'claim_definition', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['stageData', claimId, 'claim_definition']);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      setHasChanges(false);
    }
  });

  // Complete mutation
  const completeMutation = useMutation({
    mutationFn: () => completeStage(claimId, 'claim_definition'),
    onSuccess: () => {
      queryClient.invalidateQueries(['stageData', claimId]);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      if (onComplete) onComplete();
      onOpenChange(false);
    }
  });

  const handleDocumentAdded = () => {
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate({});
  };

  const handleComplete = () => {
    if (hasChanges) {
      saveMutation.mutate({}, {
        onSuccess: () => {
          completeMutation.mutate();
        }
      });
    } else {
      completeMutation.mutate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Claim Definition</span>
            {stageData.is_completed && (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            )}
          </DialogTitle>
          <DialogDescription>
            Upload or link supporting documents and evidence for this claim.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <Alert>
              <AlertDescription>
                The basic claim details (volume data, financial impact, part information) are already captured. 
                Add any additional supporting documents or evidence here.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Supporting Documents</Label>
              <DocumentSelector
                claimId={claimId}
                stageName="claim_definition"
                onDocumentAdded={handleDocumentAdded}
              />
            </div>

            {stageData.is_completed && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  This stage was completed on {new Date(stageData.completed_at).toLocaleDateString()}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {!stageData.is_completed && (
            <>
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={saveMutation.isPending || !hasChanges}
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Draft'
                )}
              </Button>
              <Button
                onClick={handleComplete}
                disabled={completeMutation.isPending}
              >
                {completeMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark Complete
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimDefinitionModal;
